import { memo, useMemo } from 'react';
import Card from '@raydiant/raydial/components/Card';
import Text from '@raydiant/raydial/components/Text';
import DisplaiProPlanImage from './images/displai-pro-plan.svg';
import { Plan, PlanFeature } from './types';
import DeviceIcon from './images/usb-drive.svg';
import LicenseIcon from './images/ticket.svg';
import FeatureIcon from './images/circle-check.svg';

interface PlanDetailsCardProps {
  plan: Plan;
}

interface FeatureItemProps {
  feature: PlanFeature;
  icon: string;
}

const FeatureItem = memo(({ feature, icon }: FeatureItemProps) => (
  <div className="flex items-center gap-2">
    <img src={icon} alt={feature.name} className="w-4 h-4" />
    <Text className="text-black">{feature.name}</Text>
  </div>
));

FeatureItem.displayName = 'FeatureItem';

const PlanDetailsCard = ({ plan }: PlanDetailsCardProps) => {
  const { deviceFeatures, licenseFeatures, regularFeatures } = useMemo(() => {
    const features = plan.features;
    return {
      deviceFeatures: features.filter((feature) => feature.type === 'device'),
      licenseFeatures: features.filter((feature) => feature.type === 'license'),
      regularFeatures: features.filter((feature) => feature.type === 'feature'),
    };
  }, [plan.features]);

  return (
    <Card className="flex flex-col flex-1 w-full md:max-w-[400px] p-0">
      <img src={DisplaiProPlanImage} alt={plan.id} className="rounded-t-2xl" />

      <div className="flex flex-col gap-6 p-8">
        <div className="flex flex-col gap-1">
          <Text className="text-black" variant="subheader">
            {plan.planTitle}
          </Text>
          <Text className="font-normal" variant="smallSubtitle">
            {plan.planDescription}
          </Text>
        </div>

        <div className="flex flex-col gap-2">
          <Text className="font-semibold text-black">
            Each {plan.name} Plan includes:
          </Text>

          {deviceFeatures.length > 0 && (
            <div className="flex flex-col gap-2">
              {deviceFeatures.map((feature) => (
                <FeatureItem
                  key={feature.id}
                  feature={feature}
                  icon={DeviceIcon}
                />
              ))}
            </div>
          )}

          {licenseFeatures.length > 0 && (
            <div className="flex flex-col gap-2">
              {licenseFeatures.map((feature) => (
                <FeatureItem
                  key={feature.id}
                  feature={feature}
                  icon={LicenseIcon}
                />
              ))}
            </div>
          )}
        </div>

        {regularFeatures.length > 0 && (
          <div className="flex flex-col gap-2">
            {regularFeatures.map((feature) => (
              <FeatureItem
                key={feature.id}
                feature={feature}
                icon={FeatureIcon}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default memo(PlanDetailsCard);
